import { useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import type { DiffRange } from '@/lib/diff/types';

export const HIGHLIGHT_ADDED = 'nula-diff-added';
export const HIGHLIGHT_DELETED = 'nula-diff-deleted';

/**
 * useDiffHighlight — applies CSS Custom Highlight API ranges to a Tiptap editor.
 *
 * Logic ported verbatim from the parked Lexical DiffHighlightPlugin:
 *   - CSS Custom Highlight API, never mutates editor DOM
 *   - buildDomRanges + TreeWalker/offset logic is identical
 *
 * Only the editor-specific bindings differ:
 *   - Lexical: editor.getRootElement() → Tiptap: editor.view.dom
 *   - Lexical: editor.registerUpdateListener(apply) → Tiptap: editor.on/off('update')
 *
 * Falls back to no-op in browsers without CSS Custom Highlight API support.
 *
 * Offset alignment: `computeDocumentDiff` (document-diff-engine.ts) extracts
 * text from `doc.getXmlFragment('prosemirror')` — the Tiptap/y-prosemirror key —
 * so the character offsets it produces align to the ProseMirror DOM text that
 * this hook walks. (It previously read the Lexical `getXmlElement('root')` key,
 * which yielded empty/misaligned offsets; fixed in e1e44a5cd.)
 */
export function useDiffHighlight(editor: Editor | null, ranges: DiffRange[]): void {
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const highlights = (CSS as any).highlights as Map<string, unknown> | undefined;
    if (!highlights) return;

    const apply = () => {
      const rootEl = editor?.view?.dom as HTMLElement | undefined;

      // !rootEl: DOM is momentarily null (e.g. editor re-mounting) — early return
      // without clearing so highlights don't flicker off and back on.
      if (!rootEl) return;

      if (ranges.length === 0) {
        highlights.delete(HIGHLIGHT_ADDED);
        highlights.delete(HIGHLIGHT_DELETED);
        return;
      }

      // Build flat list of text nodes with cumulative offsets.
      const textNodes = collectDiffTextNodes(rootEl);

      const addedRanges: Range[] = [];
      const deletedRanges: Range[] = [];

      for (const diffRange of ranges) {
        const domRanges = buildDomRanges(
          textNodes,
          diffRange.offset,
          diffRange.offset + diffRange.length,
        );
        if (diffRange.type === 'added') addedRanges.push(...domRanges);
        else deletedRanges.push(...domRanges);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hl = highlights as any;
      if (addedRanges.length > 0) hl.set(HIGHLIGHT_ADDED, new Highlight(...addedRanges));
      else hl.delete(HIGHLIGHT_ADDED);
      if (deletedRanges.length > 0) hl.set(HIGHLIGHT_DELETED, new Highlight(...deletedRanges));
      else hl.delete(HIGHLIGHT_DELETED);
    };

    apply();

    if (editor) {
      editor.on('update', apply);
    }

    return () => {
      if (editor) {
        editor.off('update', apply);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hl = (CSS as any).highlights;
      if (hl) {
        hl.delete(HIGHLIGHT_ADDED);
        hl.delete(HIGHLIGHT_DELETED);
      }
    };
  }, [editor, ranges]);
}

/**
 * Selector for custom atom node views whose React-rendered text exists in the
 * DOM but NOT in the engine's extracted text (which treats them as opaque
 * atoms). Their DOM text must be skipped so diff offsets stay aligned.
 *   - data-nula-link: renders the document title (not stored as Yjs text)
 *   - data-emoji-view: renders a sprite (no text, but skip defensively)
 */
const ATOM_NODE_VIEW_SELECTOR = '[data-nula-link], [data-emoji-view]';

/**
 * Walk the editor DOM collecting editable text nodes with cumulative offsets,
 * SKIPPING text rendered inside custom atom node views. The diff engine extracts
 * 0 chars for those atoms (their content lives in attributes/React, not the Yjs
 * text layer), so including their DOM text would shift every subsequent offset
 * and misalign the highlights.
 */
export function collectDiffTextNodes(
  rootEl: HTMLElement,
): Array<{ node: Text; start: number; end: number }> {
  const textNodes: Array<{ node: Text; start: number; end: number }> = [];
  let offset = 0;
  const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      const parent = (n as Text).parentElement;
      if (parent && parent.closest(ATOM_NODE_VIEW_SELECTOR)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    const len = node.textContent?.length ?? 0;
    textNodes.push({ node, start: offset, end: offset + len });
    offset += len;
  }
  return textNodes;
}

/** Build one DOM Range per text node that overlaps [start, end). */
export function buildDomRanges(
  textNodes: Array<{ node: Text; start: number; end: number }>,
  start: number,
  end: number,
): Range[] {
  const result: Range[] = [];
  for (const { node, start: nodeStart, end: nodeEnd } of textNodes) {
    if (nodeEnd <= start || nodeStart >= end) continue;
    const localStart = Math.max(0, start - nodeStart);
    const localEnd = Math.min(node.textContent!.length, end - nodeStart);
    if (localStart >= localEnd) continue;
    const r = document.createRange();
    r.setStart(node, localStart);
    r.setEnd(node, localEnd);
    result.push(r);
  }
  return result;
}
